import * as React from "react";
import { useContext } from "react";

import {
  StripeCapabilityStatus,
  isStripePendingVerificationForCountry,
  getBalanceAmountForCountry,
  hasPermissionOnBank,
} from "@simplyk/common";
import { inferRouterOutputs } from "@trpc/server";

import { trpc, AppRouterType } from "../helpers/trpc";

import { useCurrentUserContext } from "./CurrentUserContext";

import { IssuingCardOutput } from "@/common/types/trpc";

type StripeAccountOutput = inferRouterOutputs<AppRouterType>["getStripeCustomAccount"];

const isSupportInterventionRequirement = (requirement: string): boolean =>
  requirement.startsWith("interv_") && requirement.endsWith(".support");

export interface StripeAccountContextProps {
  stripeAccountHasRequirements: boolean;
  stripeAccountHasCollectibleRequirements: boolean;
  stripeAccountHasSupportIntervention: boolean;
  transferIsActive: boolean;
  transferIsPending: boolean;
  getStripeCustomAccountLoading: boolean;
  requirementDelay?: string;
  stripeAccountBalanceLoading: boolean;
  hasStripeCustomFlowStarted: boolean;
  isStripeCustomAccountActive: boolean;
  isStripeCustomBankConnected?: boolean;
  refetchOrganization: () => void;
  pendingCustomBalance: number;
  availableCustomBalance: number;
  stripeTotalBalance: number | null;
  refetchStripeAccount: () => void;
  isBankRequirementDue?: boolean;
  futureRequirements: string[];
  stripeCustomAccount?: StripeAccountOutput | null;
  hasAtLeastOnePayout?: boolean | null;
  hasOnlyOnePayout?: boolean | null;
  stripeCustomAccountId?: string | null;
  issuingBalance: number;
  issuingPendingBalance: number;
  nextPayoutDate?: Date;
  refetchBalance: () => void;
  stripeIssuingCard: IssuingCardOutput | null | undefined;
  refetchIssuingCard: () => void;
  isStripeIssuingCapabilityActive: boolean;
  requirementsCurrentlyDue?: string[];
  isStripePendingVerification: boolean;
  financialAccount: { hasFinancialAccount: boolean; isLoading: boolean };
  payoutsDisabledOnStripe: boolean;
  shouldRevealPayoutsPaused: "Yes" | "No" | "Unset";
  showPayoutsPausedChip: boolean;
}

export const defaultValue: StripeAccountContextProps = {
  stripeAccountHasRequirements: true,
  stripeAccountHasCollectibleRequirements: true,
  stripeAccountHasSupportIntervention: false,
  transferIsActive: true,
  transferIsPending: false,
  getStripeCustomAccountLoading: true,
  requirementDelay: "",
  stripeAccountBalanceLoading: true,
  hasStripeCustomFlowStarted: false,
  isStripeCustomAccountActive: false,
  refetchOrganization: () => {},
  pendingCustomBalance: 0,
  availableCustomBalance: 0,
  stripeTotalBalance: 0,
  refetchStripeAccount: () => {},
  isStripeCustomBankConnected: true,
  isBankRequirementDue: false,
  futureRequirements: [],
  stripeCustomAccount: null,
  hasAtLeastOnePayout: null,
  hasOnlyOnePayout: null,
  stripeCustomAccountId: null,
  issuingBalance: 0,
  issuingPendingBalance: 0,
  nextPayoutDate: undefined,
  refetchBalance: () => {},
  stripeIssuingCard: null,
  refetchIssuingCard: () => {},
  isStripeIssuingCapabilityActive: false,
  isStripePendingVerification: false,
  financialAccount: { hasFinancialAccount: false, isLoading: true },
  payoutsDisabledOnStripe: false,
  shouldRevealPayoutsPaused: "Unset",
  showPayoutsPausedChip: false,
};

export const StripeAccountContext = React.createContext<StripeAccountContextProps>(defaultValue);

export const useStripeAccountContext = (): StripeAccountContextProps => {
  return useContext(StripeAccountContext);
};

export const StripeAccountProvider: React.FunctionComponent<React.PropsWithChildren> = ({ children }) => {
  const {
    isStripeCustomAccountActive,
    hasStripeCustomFlowStarted,
    refetchOrganization,
    currentUser,
    isOrganization,
    organization,
  } = useCurrentUserContext();
  const skip = !currentUser || !isOrganization || !organization?.stripeCustomAccountId;
  const canSeeBalance = hasPermissionOnBank(currentUser?.newPermissions);
  const {
    data: getStripeCustomAccount,
    isLoading: getStripeCustomAccountLoading,
    refetch: refetchStripeAccount,
  } = trpc.getStripeCustomAccount.useQuery(undefined, { enabled: !skip });
  const {
    data: stripeAccountBalance,
    isLoading: stripeAccountBalanceLoading,
    refetch: refetchBalance,
  } = trpc.getStripeAccountBalance.useQuery(undefined, { enabled: !skip && canSeeBalance });

  const { data: getIssuingCard, refetch: refetchIssuingCard } = trpc.getTreasuryIssuingCard.useQuery(undefined, {
    enabled: !skip && Boolean(organization?.featureIssuingEnabled),
  });
  const stripeIssuingCard = getIssuingCard?.issuingCard;

  const { data: treasuryFinancialAccount } = trpc.getTreasuryFinancialAccount.useQuery(undefined, {
    enabled: !skip && Boolean(organization?.featureTreasuryEnabled),
  });

  const organizationCountry = organization?.country;
  const issuingBalance = getBalanceAmountForCountry(stripeAccountBalance?.issuing?.available, organizationCountry);
  const issuingPendingBalance = getBalanceAmountForCountry(stripeAccountBalance?.issuing?.pending, organizationCountry);

  const pendingCustomBalance = getBalanceAmountForCountry(stripeAccountBalance?.pending, organizationCountry);
  const availableCustomBalance = getBalanceAmountForCountry(stripeAccountBalance?.available, organizationCountry);
  const stripeTotalBalance = canSeeBalance ? stripeAccountBalance?.stripeTotalBalance || 0 : null;

  // TODO on filter out le future requirement "external_account": to see with product a better handling
  const futureRequirements =
    getStripeCustomAccount?.future_requirements?.currently_due?.filter(
      (requirement: string) => requirement !== "external_account"
    ) || [];

  const requirementsCurrentlyDue =
    getStripeCustomAccount?.requirements?.currently_due?.filter((requirement) => requirement !== "external_account") ||
    [];
  const stripeAccountHasSupportIntervention =
    requirementsCurrentlyDue.some(isSupportInterventionRequirement) ||
    futureRequirements.some(isSupportInterventionRequirement);
  const stripeAccountHasCollectibleRequirements =
    requirementsCurrentlyDue.some((requirement) => !isSupportInterventionRequirement(requirement)) ||
    futureRequirements.some((requirement) => !isSupportInterventionRequirement(requirement));

  const stripeAccountHasRequirements = Boolean(requirementsCurrentlyDue.length > 0 || futureRequirements?.length > 0);

  const isBankRequirementDue = getStripeCustomAccount?.requirements?.currently_due?.includes("external_account");
  const requirementsDeadline = getStripeCustomAccount?.requirements?.current_deadline;
  const requirementDelay = requirementsDeadline
    ? Math.ceil((requirementsDeadline - Date.now()) / (1000 * 60 * 60 * 24)).toString()
    : undefined;
  // Stripe pending verification only for Canadian organizations
  const isStripePendingVerification =
    Boolean(getStripeCustomAccount?.requirements?.pending_verification?.length) &&
    isStripePendingVerificationForCountry(organization?.country);

  const transferIsActive = getStripeCustomAccount?.capabilities?.transfers === StripeCapabilityStatus.Active;
  const transferIsPending = getStripeCustomAccount?.capabilities?.transfers === StripeCapabilityStatus.Pending;
  const stripeCustomAccountId = getStripeCustomAccount?.id;
  const isStripeIssuingCapabilityActive = Boolean(
    getStripeCustomAccount?.capabilities?.card_issuing === StripeCapabilityStatus.Active
  );

  const isStripeCustomBankConnected = getStripeCustomAccount?.isBankConnected;

  const stripeCustomAccount = getStripeCustomAccount;
  const nextPayoutDate = React.useMemo(
    () => (stripeAccountBalance?.nextPayoutDate ? new Date(stripeAccountBalance.nextPayoutDate) : undefined),
    [stripeAccountBalance?.nextPayoutDate]
  );

  const shouldRevealPayoutsPaused = (() => {
    switch (organization?.hubspotDisplayPayoutsPaused) {
      case true:
        return "Yes";
      case false:
        return "No";
      default:
        return "Unset";
    }
  })();

  const isStripeAccountRejected = Boolean(
    stripeCustomAccount?.isRejectedByZeffy || stripeCustomAccount?.isRejectedByStripe
  );
  const showPayoutsPausedChip =
    Boolean(organization?.payoutsDisabledOnStripe) && shouldRevealPayoutsPaused !== "No" && !isStripeAccountRejected;

  return (
    <StripeAccountContext.Provider
      value={{
        financialAccount: {
          hasFinancialAccount: Boolean(treasuryFinancialAccount),
          isLoading: !organization,
        },
        stripeAccountHasRequirements,
        stripeAccountHasCollectibleRequirements,
        stripeAccountHasSupportIntervention,
        transferIsActive,
        transferIsPending,
        getStripeCustomAccountLoading,
        issuingBalance,
        issuingPendingBalance,
        requirementDelay,
        stripeAccountBalanceLoading,
        hasStripeCustomFlowStarted,
        isStripeCustomAccountActive,
        refetchOrganization,
        pendingCustomBalance,
        availableCustomBalance,
        stripeTotalBalance,
        stripeCustomAccount,
        refetchStripeAccount,
        isStripeCustomBankConnected,
        isBankRequirementDue,
        futureRequirements,
        hasAtLeastOnePayout: getStripeCustomAccount?.hasAtLeastOnePayout,
        hasOnlyOnePayout: getStripeCustomAccount?.hasOnlyOnePayout,
        stripeCustomAccountId,
        nextPayoutDate,
        refetchBalance,
        stripeIssuingCard,
        refetchIssuingCard,
        isStripeIssuingCapabilityActive,
        requirementsCurrentlyDue,
        isStripePendingVerification,
        payoutsDisabledOnStripe: Boolean(organization?.payoutsDisabledOnStripe),
        shouldRevealPayoutsPaused,
        showPayoutsPausedChip,
      }}
    >
      {children}
    </StripeAccountContext.Provider>
  );
};
